import { Grid } from '@mui/material';
import { useFormikContext } from 'formik';
import { type ReactElement, useEffect } from 'react';

import {
  PRICE_ESCALATION_FLAT_MAX_INTEGER_DIGITS,
  PRICE_ESCALATION_PERCENTAGE_DECIMAL_PLACES,
  PRICE_ESCALATION_PERCENTAGE_MAX_INTEGER_DIGITS,
} from '@core/constants/app';
import Enums from '@core/types/api/Enums';
import type { QuotePriceEscalationWireFormat } from '@core/types/api/QuotePriceEscalation/QuotePriceEscalation';
import { requiredLabel } from '@core/utils/strings';

import { Input } from '../Input/Input';
import { InputSwitch } from '../InputSwitch/InputSwitch';

type PriceEscalationInputProps = {
  /** The formik name of the high-level price escalation. */
  name: string;
  readOnly?: boolean;
  currencySymbol?: string;
  /**
  When set, enforces a single escalation kind for the entire quote.
  Individual price escalations cannot override this value and the UI
  must hide or disable any per-escalation kind selection.
  */
  enforcedKind?: Enums.EscalationKind;
};

export const PriceEscalationInput = ({
  name,
  readOnly,
  currencySymbol = '$',
  enforcedKind,
}: PriceEscalationInputProps): ReactElement => {
  const formikBag = useFormikContext();

  const formikKind = formikBag.getFieldMeta<QuotePriceEscalationWireFormat>(name).value.kind;

  const currentKind = enforcedKind ?? formikKind;
  const isPercentage = currentKind === Enums.EscalationKind.Percentage;

  useEffect(() => {
    if (enforcedKind && formikKind !== enforcedKind) {
      formikBag.setFieldValue(`${name}.kind`, enforcedKind);
    }
  }, [enforcedKind, formikKind, name, formikBag]);

  const percentageInputProps = {
    readOnly,
    name: `${name}.changeRatio`,
    type: 'number' as const,
    label: requiredLabel('Percentage Change', isPercentage),
    precision: PRICE_ESCALATION_PERCENTAGE_DECIMAL_PLACES,
    endAdornment: '%',
    maxIntegerDigits: PRICE_ESCALATION_PERCENTAGE_MAX_INTEGER_DIGITS,
  };

  const flatInputProps = {
    readOnly,
    name: `${name}.flatChange`,
    type: 'currency' as const,
    label: requiredLabel('Flat Amount', !isPercentage),
    startAdornment: currencySymbol,
    maxIntegerDigits: PRICE_ESCALATION_FLAT_MAX_INTEGER_DIGITS,
  };

  return (
    <Grid container spacing={2} alignItems='flex-end'>
      <Grid item xs={4}>
        <Input
          name={`${name}.escalationDate`}
          label='Escalation date*'
          type='date'
          readOnly={readOnly}
        />
      </Grid>
      <Grid item xs={8}>
        {enforcedKind ? (
          <Input {...(isPercentage ? percentageInputProps : flatInputProps)} />
        ) : (
          <InputSwitch
            readOnly={readOnly}
            switchFormikName={`${name}.kind`}
            leftLabel='Percentage Change'
            rightLabel='Flat Amount'
            leftValue={Enums.EscalationKind.Percentage}
            rightValue={Enums.EscalationKind.Flat}
            leftInputProps={percentageInputProps}
            rightInputProps={flatInputProps}
          />
        )}
      </Grid>
    </Grid>
  );
};
